// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ExternalHyperlink,
} from "docx";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { saveAs } from "file-saver";

async function fetchImageData(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return new Uint8Array(arrayBuffer);
}

const parseTextWithFormatting = async (
  text: string
): Promise<(TextRun | ImageRun | ExternalHyperlink)[]> => {
  const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|https?:\/\/\S+|\[\d+\]\(https?:\/\/\S+\))/g);
  const textRuns: (TextRun | ImageRun | ExternalHyperlink)[] = [];
  let lastWasUrl = false;
  
  // Find all URL positions to determine the last URL
  const urlIndices = [];
  for (let i = 0; i < parts.length; i++) {
    if (/^\[\d+\]\(https?:\/\/\S+\)$/.test(parts[i]) || (/^https?:\/\//.test(parts[i]) && !parts[i].includes(".png") && !parts[i].includes(".jpg") && !parts[i].includes(".jpeg") && !parts[i].includes(".gif") && !parts[i].includes("blob.core.windows.net"))) {
      urlIndices.push(i);
    }
  }

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const isLastUrl = urlIndices.length > 1 && i === urlIndices[urlIndices.length - 1];
    
    if (part.startsWith("**") && part.endsWith("**")) {
      textRuns.push(new TextRun({ text: part.slice(2, -2), bold: true }));
      lastWasUrl = false;
    } else if (
      part.startsWith("*") &&
      part.endsWith("*") &&
      !part.startsWith("**")
    ) {
      textRuns.push(new TextRun({ text: part.slice(1, -1), italics: true }));
      lastWasUrl = false;
    } else if (/^\[\d+\]\(https?:\/\/\S+\)$/.test(part)) {
      const match = part.match(/^\[(\d+)\]\((https?:\/\/\S+)\)$/);
      if (match) {
        if (lastWasUrl) {
          textRuns.push(new TextRun({ text: isLastUrl ? " and " : ", " }));
        }
        textRuns.push(
          new ExternalHyperlink({
            children: [new TextRun({ text: match[2], style: "Hyperlink" })],
            link: match[2],
          })
        );
        lastWasUrl = true;
      }
    } else if (
      /^https?:\/\//.test(part) &&
      (part.includes(".png") ||
        part.includes(".jpg") ||
        part.includes(".jpeg") ||
        part.includes(".gif") ||
        part.includes("blob.core.windows.net"))
    ) {
      try {
        const imageData = await fetchImageData(part);
        textRuns.push(
          new ImageRun({
            data: imageData,
            transformation: {
              width: 600,
              height: 600,
            },
            type: "png",
          })
        );
      } catch (error) {
        textRuns.push(new TextRun({ text: `[Image: ${part}]` }));
      }
      lastWasUrl = false;
    } else if (/^https?:\/\//.test(part)) {
      if (lastWasUrl) {
        textRuns.push(new TextRun({ text: isLastUrl ? " and " : ", " }));
      }
      textRuns.push(
        new ExternalHyperlink({
          children: [new TextRun({ text: part, style: "Hyperlink" })],
          link: part,
        })
      );
      lastWasUrl = true;
    } else if (part && part.trim()) {
      textRuns.push(new TextRun({ text: part }));
      lastWasUrl = false;
    } else if (part === " ") {
      if (!lastWasUrl) {
        textRuns.push(new TextRun({ text: part }));
      }
    }
  }

  return textRuns;
};

const formatTextToParagraphs = async (text: string): Promise<(Paragraph | Table)[]> => {
  const lines = text.split("\n");
  const elements: (Paragraph | Table)[] = [];
  let i = 0;

  while (i < lines.length) {
    const trimmedLine = lines[i].trim();

    // Check for table
    if (trimmedLine.includes("|") && i + 1 < lines.length && lines[i + 1].includes("|")) {
      const tableLines = [];
      let j = i;
      while (j < lines.length && lines[j].trim().includes("|")) {
        tableLines.push(lines[j].trim());
        j++;
      }
      
      if (tableLines.length >= 2) {
        const headerRow = tableLines[0].split("|").map(cell => cell.trim()).filter(cell => cell);
        const rows = tableLines.slice(2).map(row => 
          row.split("|").map(cell => cell.trim()).filter(cell => cell)
        );
        
        const headerCells = await Promise.all(
          headerRow.map(async cell => 
            new TableCell({
              children: [new Paragraph({ children: await parseTextWithFormatting(cell) })]
            })
          )
        );
        
        const dataRows = await Promise.all(
          rows.map(async row => {
            const cells = await Promise.all(
              row.map(async cell => 
                new TableCell({
                  children: [new Paragraph({ children: await parseTextWithFormatting(cell) })]
                })
              )
            );
            return new TableRow({ children: cells });
          })
        );
        
        const table = new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new TableRow({ children: headerCells }),
            ...dataRows
          ]
        });
        
        elements.push(table);
        i = j;
        continue;
      }
    }

    if (trimmedLine === "") {
      elements.push(new Paragraph({ text: "" }));
    } else if (trimmedLine.startsWith("# ")) {
      const headingText = trimmedLine.substring(2);
      elements.push(
        new Paragraph({
          children: await parseTextWithFormatting(headingText),
          heading: HeadingLevel.HEADING_1,
        })
      );
    } else if (trimmedLine.startsWith("## ")) {
      const headingText = trimmedLine.substring(3);
      elements.push(
        new Paragraph({
          children: await parseTextWithFormatting(headingText),
          heading: HeadingLevel.HEADING_2,
        })
      );
    } else if (trimmedLine.startsWith("### ")) {
      const headingText = trimmedLine.substring(4);
      elements.push(
        new Paragraph({
          children: await parseTextWithFormatting(headingText),
          heading: HeadingLevel.HEADING_3,
        })
      );
    } else if (trimmedLine.startsWith("- ")) {
      const bulletText = trimmedLine.substring(2);
      elements.push(
        new Paragraph({
          children: await parseTextWithFormatting(bulletText),
          bullet: {
            level: 0,
          },
        })
      );
    } else {
      elements.push(
        new Paragraph({
          children: await parseTextWithFormatting(trimmedLine),
        })
      );
    }
    i++;
  }

  return elements;
};

interface ChatPair {
  userQuery: string;
  aiResponse: string;
  timestamp?: string;
  model?: string;
}

export const generateAndDownloadDocx = async (
  userQuery: string,
  aiResponse: string,
  model?: string
) => {
  const timestamp = new Date().toLocaleString();

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            text: "Chat Conversation Export",
            heading: HeadingLevel.HEADING_1,
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Exported on: ${timestamp}`,
                italics: true,
              }),
            ],
          }),
          new Paragraph({
            text: "",
          }),
          new Paragraph({
            text: "User Query:",
            heading: HeadingLevel.HEADING_2,
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: userQuery,
              }),
            ],
          }),
          new Paragraph({
            text: "",
          }),
          new Paragraph({
            text: "AI Response:",
            heading: HeadingLevel.HEADING_2,
          }),
          ...(await formatTextToParagraphs(aiResponse)),

        ],
      },
    ],
  });

  try {
    const blob = await Packer.toBlob(doc);
    const fileName = `chat-conversation-${new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/:/g, "-")}.docx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error generating DOCX:", error);
    throw new Error("Failed to generate document");
  }
};
